import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getLabOrder, updateLabStatus, postLabResult,
  updateLabOrderItem, updateLabOrderItemAnalyte,
} from '../../api/orders.js'
import { listDocuments, uploadDocument } from '../../api/documents.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CrossFacilityErrorCard from '../../components/CrossFacilityErrorCard.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'
import { LAB_TRANSITIONS, ORDER_STATUS } from '../../constants/index.js'

/**
 * Laboratory order detail.
 *
 * The lab tech uses this page end-to-end for a single sample:
 *   1. Accept the order (pending → accepted) after receiving the specimen.
 *   2. Start work (accepted → in_progress) when analysis begins.
 *   3. Enter a short result summary the doctor/patient will see, and
 *      attach the full result file (PDF/image/CSV) produced by the
 *      analyser. Uploads go to documents.ClinicalDocument bound to this
 *      LabOrder, so the doctor and patient can open them from their
 *      own documents views.
 *   4. Mark complete when everything's uploaded.
 */
export default function LabOrderDetail() {
  const { orderId } = useParams()
  const qc = useQueryClient()
  const [comment, setComment] = useState('')              // status-change comment
  const [summary, setSummary] = useState('')              // result_summary (final)
  // NEW lab-side fields driving the doctor's lifecycle UX:
  //   resultStatus = 'pending' | 'preliminary' | 'final'
  //   prelim       = quick-read summary entered ASAP for empirical Tx
  //   labComment   = free-text notes (specimen quality, repeat suggested)
  const [resultStatus, setResultStatus] = useState('pending')
  const [prelim, setPrelim] = useState('')
  const [labComment, setLabComment] = useState('')

  const q = useQuery({
    queryKey: ['lab-order', orderId],
    queryFn: () => getLabOrder(orderId),
    enabled: Boolean(orderId),
  })
  const order = q.data

  // Attached result files for this lab order (polymorphic ClinicalDocument).
  const docsQ = useQuery({
    queryKey: ['lab-order-docs', orderId],
    queryFn: () =>
      listDocuments({
        owner_model: 'clinical_notes.LabOrder',
        owner_id: orderId,
      }),
    enabled: Boolean(orderId),
  })

  // Track last-saved + flash state so the lab tech sees the loop close.
  // Identical pattern to RadiologyOrderDetail. Declared BEFORE the
  // useEffect that uses them so the const-before-use ordering is clean.
  const [lastSavedSummary, setLastSavedSummary] = useState('')
  const [resultSavedFlash, setResultSavedFlash] = useState(false)

  // Keep result fields in sync with the server snapshot. The "&& !x"
  // guards mean we don't clobber unsaved typing when a refetch lands.
  useEffect(() => {
    if (order?.result_summary && !summary) setSummary(order.result_summary)
    if (order?.preliminary_result_summary && !prelim) setPrelim(order.preliminary_result_summary)
    if (order?.result_comment && !labComment) setLabComment(order.result_comment)
    if (order?.result_status) setResultStatus(order.result_status)
    if (order?.result_summary != null) {
      setLastSavedSummary((order.result_summary || '').trim())
    }
  }, [order]) // eslint-disable-line react-hooks/exhaustive-deps

  const statusMut = useMutation({
    mutationFn: ({ next, comment }) => updateLabStatus(orderId, next, comment),
    onSuccess: (fresh) => {
      qc.setQueryData(['lab-order', orderId], fresh)
      qc.invalidateQueries({ queryKey: ['lab-queue'] })
      setComment('')
    },
  })
  const resultMut = useMutation({
    mutationFn: (body) => postLabResult(orderId, body),
    onSuccess: (fresh) => {
      qc.setQueryData(['lab-order', orderId], fresh)
      qc.invalidateQueries({ queryKey: ['lab-queue'] })
      setLastSavedSummary((fresh?.result_summary || '').trim())
      setResultSavedFlash(true)
      setTimeout(() => setResultSavedFlash(false), 2500)
    },
  })

  // ---- result-file upload ---------------------------------------------------
  const [file, setFile] = useState(null)
  const [fileDesc, setFileDesc] = useState('')
  const uploadMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a result file first.')
      return uploadDocument({
        owner_model: 'clinical_notes.LabOrder',
        owner_id: orderId,
        file,
        kind: 'lab_result',
        description: fileDesc,
      })
    },
    onSuccess: () => {
      setFile(null)
      setFileDesc('')
      qc.invalidateQueries({ queryKey: ['lab-order-docs', orderId] })
    },
  })

  // Tab state — declared BEFORE any early return so the hook count
  // is constant across renders (Rules of Hooks). Default tab is
  // Result because that's the lab tech's main work surface.
  const [tab, setTab] = useState('result')

  if (q.isLoading) return <div className="p-4 text-sm text-slate-400">Loading…</div>
  if (q.isError || !order) {
    // Cross-laboratory 403 → CrossFacilityErrorCard renders the tel:
    // call screen so the lab tech can phone the correct facility.
    const env = q.error?.response?.data?.error
    if (env?.code === 'cross_laboratory_access') {
      return (
        <div className="space-y-3">
          <CrossFacilityErrorCard
            error={q.error}
            backTo="/lab"
            backLabel="Back to queue"
          />
        </div>
      )
    }
    return (
      <div className="space-y-3">
        <ErrorBanner error={q.error || new Error('Order not found')} onRetry={() => q.refetch()} />
        <Link to="/lab" className="text-xs underline">Back to queue</Link>
      </div>
    )
  }

  const statusLc = (order.status || '').toLowerCase()
  const nexts = LAB_TRANSITIONS[statusLc] || []
  // Results (summary text + file upload) are available once the lab has
  // actually accepted the work — i.e. not while pending/rejected/cancelled.
  const canEnterResult = [
    ORDER_STATUS.ACCEPTED,
    ORDER_STATUS.IN_PROGRESS,
    ORDER_STATUS.COMPLETED,
  ].includes(statusLc)

  const docs = docsQ.data || []

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Lab order</div>
            <div className="font-semibold">{order.patient_name || 'Patient'}</div>
            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
              <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(order.status)}`}>{order.status}</span>
              <span>Requested {formatDateTime(order.created_at)}</span>
              {order.laboratory_name && <span>· {order.laboratory_name}</span>}
            </div>
          </div>
          <Link to="/lab" className="text-xs underline">Back</Link>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Test results"
          subtitle={canEnterResult
            ? "Enter a value, unit and reference range against each test. Set the abnormal flag — H/L/HH/LL/A — to highlight clinically actionable results in red on the doctor's screen."
            : "These tests will become editable once you accept this order from the queue."}
        />
        <TestResultTable
          items={order.items || []}
          orderId={orderId}
          editable={canEnterResult}
        />
        {order.clinical_notes && (
          <div className="mt-3 text-xs text-slate-500 border-t pt-3">
            <div className="uppercase tracking-wide text-[10px] text-slate-400">Ordering doctor's clinical notes</div>
            <div className="mt-1 whitespace-pre-wrap">{order.clinical_notes}</div>
          </div>
        )}
      </Card>

      {/* ─── Tab strip — Sign-off / Files / Workflow ────────────────
          Test results table above stays always-visible (lab tech needs
          to see the analyte grid while doing every action). Below it
          the tabs split the action surfaces so the user isn't scrolling
          past Files to reach Sign-off. */}
      {(() => {
        const TABS = [
          { id: 'result',   label: 'Sign-off' },
          { id: 'files',    label: 'Files' },
          { id: 'workflow', label: 'Workflow', hidden: nexts.length === 0 },
        ].filter((t) => !t.hidden)
        return (
          <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
            {TABS.map((t) => {
              const active = tab === t.id
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={
                    `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                    (active
                      ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                      : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
                  }
                >
                  {t.label}
                </button>
              )
            })}
          </div>
        )
      })()}

      {tab === 'workflow' && nexts.length > 0 && (
        <Card>
          <CardHeader title="Workflow actions" subtitle="Move the order through its admin lifecycle. The ordering clinician sees a colour-coded badge driven by these transitions." />
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {nexts.map(({ label, next }) => (
              <button
                key={next}
                onClick={() => statusMut.mutate({ next, comment })}
                disabled={statusMut.isPending}
                className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
              >
                {label}
              </button>
            ))}
          </div>
          {statusMut.isError && <div className="mt-2"><ErrorBanner error={statusMut.error} /></div>}
        </Card>
      )}

      {tab === 'result' && canEnterResult && (
        <>
          <Card>
            <CardHeader
              title="Result"
              subtitle="Set the lifecycle status, the doctor sees a colour-coded badge driven by this. Preliminary text becomes 'View Results' for empirical-treatment decisions; the formal summary + uploaded files are the authoritative record."
            />

            {/* Status selector — drives the doctor's badge */}
            <div className="mb-3">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Status</label>
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
                {[
                  { v: 'pending',     label: 'Pending',     tone: 'bg-slate-100 text-slate-700' },
                  { v: 'preliminary', label: 'Preliminary', tone: 'bg-sky-100 text-sky-800' },
                  { v: 'final',       label: 'Final',       tone: 'bg-emerald-100 text-emerald-800' },
                ].map((s, i) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setResultStatus(s.v)}
                    className={
                      'px-4 py-2 font-semibold border-r border-slate-300 last:border-r-0 transition ' +
                      (resultStatus === s.v ? s.tone : 'bg-white text-slate-500 hover:bg-slate-50')
                    }
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Set <b>Preliminary</b> as soon as you have an interpretable result — the doctor can then start empirical treatment while you finish the formal report.
              </div>
            </div>

            {/* Preliminary summary */}
            <div className="mb-3">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Preliminary result summary</label>
              <textarea
                rows={3}
                value={prelim}
                onChange={(e) => setPrelim(e.target.value)}
                placeholder="e.g. WBC elevated with neutrophilia. Cardiac enzymes normal. Awaiting culture."
                className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Final summary (existing field) */}
            <div className="mb-3">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Final result summary</label>
              <textarea
                rows={4}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="e.g. FBC: WBC 14.2 (H), Hb 11.8, Plt 220. Mild neutrophilia — consistent with bacterial infection."
                className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {/* Lab comment */}
            <div className="mb-3">
              <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">Lab comments (optional)</label>
              <textarea
                rows={2}
                value={labComment}
                onChange={(e) => setLabComment(e.target.value)}
                placeholder="e.g. Slight haemolysis on sample, repeat if clinically indicated."
                className="w-full border border-slate-300 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <div className="mt-2 flex items-center justify-end gap-3">
              {resultMut.isPending && (
                <span className="text-xs text-slate-500">Saving…</span>
              )}
              {!resultMut.isPending && resultSavedFlash && (
                <span className="text-xs font-semibold text-emerald-700">Saved ✓</span>
              )}
              <button
                onClick={() => resultMut.mutate({
                  summary, prelim, comment: labComment, result_status: resultStatus,
                })}
                disabled={resultMut.isPending}
                className="text-sm font-semibold bg-wantok-700 hover:bg-wantok-800 disabled:bg-slate-300 text-white rounded-lg px-4 py-2 transition"
              >
                {resultMut.isPending ? 'Saving…' : 'Save result'}
              </button>
            </div>
            {resultMut.isError && <div className="mt-2"><ErrorBanner error={resultMut.error} /></div>}
          </Card>
        </>
      )}

      {tab === 'files' && canEnterResult && (
        <>
          <Card>
            <CardHeader
              title="Result files"
              subtitle="Attach the analyser print-out or a scanned report (PDF/image/CSV). The doctor and patient will be able to open these."
            />
            {docsQ.error && <ErrorBanner error={docsQ.error} onRetry={() => docsQ.refetch()} />}
            <ul className="divide-y divide-slate-100">
              {docsQ.isLoading && (
                <li className="py-3 text-sm text-slate-400">Loading attachments…</li>
              )}
              {!docsQ.isLoading && docs.length === 0 && (
                <li className="py-3 text-sm text-slate-400">No files uploaded yet.</li>
              )}
              {docs.map((d) => (
                <li key={d.id} className="py-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {d.original_filename || `Document #${d.id}`}
                    </div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                      <span className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-slate-50 border-slate-200 text-slate-600">
                        {d.kind || 'lab_result'}
                      </span>
                      {d.size_bytes != null && <span>{Math.round(d.size_bytes / 1024)} KB</span>}
                      {d.mime_type && <span>{d.mime_type}</span>}
                      <span>Uploaded {formatDateTime(d.uploaded_at)}</span>
                      {d.uploaded_by_username && <span>· by {d.uploaded_by_username}</span>}
                    </div>
                    {d.description && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{d.description}</div>
                    )}
                  </div>
                  {d.file_url && (
                    <a
                      href={d.file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-wantok-700 underline shrink-0"
                    >
                      Open
                    </a>
                  )}
                </li>
              ))}
            </ul>

            {/* Upload row sits flush below the file list — the card title
                "Result files" already establishes the context, so a
                second "Upload a result file" sub-heading would just
                duplicate it. */}
            <div className="border-t pt-3 mt-3 space-y-2">
              <div className="grid gap-2 md:grid-cols-[1fr_14rem_6rem] items-center">
                <input
                  type="file"
                  className="text-sm"
                  accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.csv,.xlsx,.xls,.txt"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <input
                  type="text"
                  placeholder="Description (e.g. FBC analyser printout)"
                  value={fileDesc}
                  onChange={(e) => setFileDesc(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                />
                <button
                  onClick={() => uploadMut.mutate()}
                  disabled={!file || uploadMut.isPending}
                  className="px-3 py-1 bg-wantok-700 text-white text-sm rounded disabled:bg-slate-400"
                >
                  {uploadMut.isPending ? 'Uploading…' : 'Upload'}
                </button>
              </div>
              {uploadMut.isError && (
                <ErrorBanner error={uploadMut.error} />
              )}
              <div className="text-[11px] text-slate-400">
                Max 25 MB per file. Files attach to this lab order and become
                visible to the ordering doctor and the patient.
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}

/* ===========================================================================
 *  Test result table
 *
 *  Replaces the static "list of test names + LOINC + ROUTINE pill" with a
 *  real lab-report-style table. Each ordered test gets a row where the lab
 *  tech can fill in:
 *
 *      Test name   |  Value          |  Unit       |  Reference range  |  Flag
 *      CBC         |  14.2           |  x10⁹/L     |  4.0-11.0         |  [H]   ← row turns red
 *      Malaria RDT |  Positive       |             |  Negative          |  [A]   ← row turns red
 *      …
 *
 *  Save model: per-row save-on-blur. The lab tech tabs through fields and
 *  each row PATCHes the moment focus leaves any of its inputs (after a
 *  short coalesce). The "Saved ✓" flash on the row tells them the loop
 *  closed without forcing a giant "Save all rows" button at the bottom.
 *
 *  Read-only mode: when canEnterResult is false (order still pending or
 *  rejected) the same table renders with disabled inputs so the structure
 *  is consistent and the lab tech sees what they'll need to fill in once
 *  they accept the order.
 * ========================================================================= */

const ABNORMAL_FLAG_OPTIONS = [
  { v: '',   label: '—',   tone: 'bg-slate-50 text-slate-500 border-slate-200' },
  { v: 'L',  label: 'L',   tone: 'bg-amber-50 text-amber-800 border-amber-300' },
  { v: 'H',  label: 'H',   tone: 'bg-amber-50 text-amber-800 border-amber-300' },
  { v: 'LL', label: 'LL',  tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  { v: 'HH', label: 'HH',  tone: 'bg-rose-100 text-rose-800 border-rose-300' },
  { v: 'A',  label: 'A',   tone: 'bg-rose-100 text-rose-800 border-rose-300' },
]

function flagTone(flag) {
  const opt = ABNORMAL_FLAG_OPTIONS.find((o) => o.v === flag)
  return opt ? opt.tone : ABNORMAL_FLAG_OPTIONS[0].tone
}

function TestResultTable({ items, orderId, editable }) {
  if (!items.length) {
    return <div className="py-6 text-sm text-slate-400">No tests on this order.</div>
  }
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="text-left font-semibold px-3 py-2">Test / Analyte</th>
            <th className="text-left font-semibold px-3 py-2 w-32">Value</th>
            <th className="text-left font-semibold px-3 py-2 w-24">Unit</th>
            <th className="text-left font-semibold px-3 py-2 w-32">Ref. range</th>
            <th className="text-left font-semibold px-3 py-2 w-20">Flag</th>
            <th className="text-right font-semibold px-3 py-2 w-16">Saved</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            it.is_panel
              ? (
                <PanelResultBlock
                  key={it.id || it.test_code}
                  item={it}
                  orderId={orderId}
                  editable={editable}
                />
              )
              : (
                <TestResultRow
                  key={it.id || it.test_code}
                  item={it}
                  orderId={orderId}
                  editable={editable}
                />
              )
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TestResultRow({ item, orderId, editable }) {
  const qc = useQueryClient()
  // Local copies so typing is responsive without round-tripping through
  // server state. Initial values come from the item the parent passed in.
  const [value, setValue] = useState(item.result_value || '')
  const [unit, setUnit]   = useState(item.result_unit  || '')
  const [ref, setRef]     = useState(item.reference_range || '')
  const [flag, setFlag]   = useState(item.abnormal_flag || '')
  const [savedFlash, setSavedFlash] = useState(false)
  const [error, setError] = useState(null)

  // If the parent re-fetches and gives us a fresher row (e.g. the lab
  // tech reloaded), respect that — but only when our local fields match
  // the previously-known server state, so we don't clobber unsaved
  // typing.
  useEffect(() => {
    setValue((v) => (v === '' || v === item.result_value) ? (item.result_value || '') : v)
    setUnit((u)  => (u === '' || u === item.result_unit)  ? (item.result_unit  || '') : u)
    setRef((r)   => (r === '' || r === item.reference_range) ? (item.reference_range || '') : r)
    setFlag((f)  => (f === item.abnormal_flag) ? (item.abnormal_flag || '') : f)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.result_value, item.result_unit, item.reference_range, item.abnormal_flag])

  const mut = useMutation({
    mutationFn: (patch) => updateLabOrderItem(item.id, patch),
    onSuccess: () => {
      setError(null)
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1800)
      // Update the parent LabOrder cache so the doctor-side polled view
      // sees the new fields without a full refetch.
      qc.invalidateQueries({ queryKey: ['lab-order', orderId] })
    },
    onError: (e) => setError(e),
  })

  // Save the row only when the user actually changed something — avoids
  // PATCHing on a mere focus-bounce that didn't touch any value.
  function persistIfDirty() {
    if (!editable || !item.id) return
    const dirty =
      value !== (item.result_value || '') ||
      unit  !== (item.result_unit  || '') ||
      ref   !== (item.reference_range || '') ||
      flag  !== (item.abnormal_flag || '')
    if (!dirty) return
    mut.mutate({
      result_value: value,
      result_unit: unit,
      reference_range: ref,
      abnormal_flag: flag,
    })
  }

  // Whole-row tint when the saved-on-server flag indicates an abnormal
  // finding. Driven off the ITEM (server truth), not off our local
  // unsaved `flag`, so the visual stays honest.
  const savedFlag = item.abnormal_flag || ''
  const isAbnormal = ['L', 'H', 'LL', 'HH', 'A'].includes(savedFlag)
  const isCritical = ['LL', 'HH', 'A'].includes(savedFlag)
  const rowTint = isCritical
    ? 'bg-rose-50/70'
    : isAbnormal
      ? 'bg-amber-50/50'
      : ''

  const inputClass =
    'w-full border border-slate-300 focus:border-wantok-600 focus:outline-none ' +
    'rounded px-2 py-1.5 text-sm disabled:bg-slate-100 disabled:text-slate-500'

  return (
    <tr className={`border-b border-slate-100 align-top ${rowTint}`}>
      {/* Test column — name + small LOINC underneath so the row stays compact */}
      <td className="px-3 py-2">
        <div className="font-medium text-slate-800">{item.test_name}</div>
        <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-2">
          {item.test_code && <span className="font-mono">{item.test_code}</span>}
          {item.loinc_code && <span>LOINC {item.loinc_code}</span>}
          {item.urgency && item.urgency !== 'routine' && (
            <span className="uppercase font-semibold text-rose-700">{item.urgency}</span>
          )}
        </div>
      </td>

      <td className="px-3 py-2">
        <input
          type="text"
          value={value}
          disabled={!editable}
          onChange={(e) => setValue(e.target.value)}
          onBlur={persistIfDirty}
          placeholder="14.2 / Negative"
          className={inputClass}
        />
      </td>

      <td className="px-3 py-2">
        <input
          type="text"
          value={unit}
          disabled={!editable}
          onChange={(e) => setUnit(e.target.value)}
          onBlur={persistIfDirty}
          placeholder="x10⁹/L"
          className={inputClass}
        />
      </td>

      <td className="px-3 py-2">
        <input
          type="text"
          value={ref}
          disabled={!editable}
          onChange={(e) => setRef(e.target.value)}
          onBlur={persistIfDirty}
          placeholder="4.0-11.0"
          className={inputClass}
        />
      </td>

      <td className="px-3 py-2">
        <select
          value={flag}
          disabled={!editable}
          onChange={(e) => {
            const next = e.target.value
            setFlag(next)
            // Save flag immediately (it's a single click, no need for blur)
            if (editable && item.id) {
              mut.mutate({
                result_value: value,
                result_unit: unit,
                reference_range: ref,
                abnormal_flag: next,
              })
            }
          }}
          className={
            'w-full border rounded px-2 py-1.5 text-sm font-bold text-center ' +
            'disabled:opacity-60 ' +
            flagTone(flag)
          }
        >
          {ABNORMAL_FLAG_OPTIONS.map((opt) => (
            <option key={opt.v || 'none'} value={opt.v}>{opt.label}</option>
          ))}
        </select>
      </td>

      <td className="px-3 py-2 text-right">
        {mut.isPending && <span className="text-[11px] text-slate-400">…</span>}
        {!mut.isPending && savedFlash && (
          <span className="text-[11px] font-semibold text-emerald-700">✓</span>
        )}
        {error && (
          <div title={error.message} className="text-[10px] text-rose-700 truncate">
            ⚠ save
          </div>
        )}
      </td>
    </tr>
  )
}

/* ===========================================================================
 *  Panel result block — header row + N analyte sub-rows
 *
 *  Used when item.is_panel is true (FBC, UEC, LFT, lipid, TFT, etc.).
 *  The parent LabOrderItem itself has no result — values live on each
 *  LabOrderItemAnalyte child. The header row stays clickable so the lab
 *  tech can collapse a long panel (FBC = 14 analytes) when working
 *  through a multi-panel order.
 * ========================================================================= */
function PanelResultBlock({ item, orderId, editable }) {
  // Default expanded so the lab tech sees the full FBC/UEC immediately.
  // Stored per-item in localStorage so collapse state survives a refresh.
  const storageKey = `lab-panel-${item.id}-collapsed`
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1' } catch { return false }
  })
  function toggle() {
    setCollapsed((c) => {
      const next = !c
      try { localStorage.setItem(storageKey, next ? '1' : '0') } catch {}
      return next
    })
  }

  const analytes = item.analytes || []
  // Count abnormal analytes for the header summary chip — gives the
  // tech a one-glance answer to "did anything flag?" even when
  // collapsed.
  const abnormalCount = analytes.filter(
    (a) => ['L', 'H', 'LL', 'HH', 'A'].includes(a.abnormal_flag || '')
  ).length

  return (
    <>
      {/* Panel header row — visually distinct from the analyte rows */}
      <tr className="bg-slate-50 border-b border-slate-200">
        <td colSpan={6} className="px-3 py-2">
          <button
            type="button"
            onClick={toggle}
            className="w-full text-left flex items-center gap-2 hover:bg-slate-100/60 -mx-1 px-1 rounded"
          >
            <span className="text-slate-400 text-[11px] font-mono w-3">
              {collapsed ? '▸' : '▾'}
            </span>
            <span className="font-bold text-slate-800">{item.test_name}</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">
              · panel · {analytes.length} analyte{analytes.length === 1 ? '' : 's'}
            </span>
            {item.test_code && (
              <span className="text-[10px] font-mono text-slate-400">{item.test_code}</span>
            )}
            {item.urgency && item.urgency !== 'routine' && (
              <span className="text-[10px] uppercase font-semibold text-rose-700 ml-1">
                {item.urgency}
              </span>
            )}
            {abnormalCount > 0 && (
              <span className="ml-auto text-[10px] uppercase font-bold text-rose-700 bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5">
                {abnormalCount} abnormal
              </span>
            )}
          </button>
        </td>
      </tr>

      {/* Analyte sub-rows */}
      {!collapsed && analytes.map((a) => (
        <AnalyteResultRow
          key={a.id}
          analyte={a}
          orderId={orderId}
          editable={editable}
        />
      ))}
      {!collapsed && analytes.length === 0 && (
        <tr><td colSpan={6} className="px-3 py-2 text-[12px] text-slate-400 italic">
          No analytes were instantiated for this panel — catalogue may be missing a template.
        </td></tr>
      )}
    </>
  )
}

/* ===========================================================================
 *  Analyte result row — one analyte under a panel
 *
 *  Same shape as TestResultRow but PATCHes the dedicated
 *  /lab-order-item-analytes/{id}/ endpoint, and the test column shows
 *  the analyte name (indented) instead of the parent test name.
 * ========================================================================= */
function AnalyteResultRow({ analyte, orderId, editable }) {
  const qc = useQueryClient()
  const [value, setValue] = useState(analyte.result_value || '')
  const [unit, setUnit]   = useState(analyte.result_unit  || '')
  const [ref, setRef]     = useState(analyte.reference_range || '')
  const [flag, setFlag]   = useState(analyte.abnormal_flag || '')
  const [savedFlash, setSavedFlash] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    setValue((v) => (v === '' || v === analyte.result_value) ? (analyte.result_value || '') : v)
    setUnit((u)  => (u === '' || u === analyte.result_unit)  ? (analyte.result_unit  || '') : u)
    setRef((r)   => (r === '' || r === analyte.reference_range) ? (analyte.reference_range || '') : r)
    setFlag((f)  => (f === analyte.abnormal_flag) ? (analyte.abnormal_flag || '') : f)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyte.id, analyte.result_value, analyte.result_unit, analyte.reference_range, analyte.abnormal_flag])

  const mut = useMutation({
    mutationFn: (patch) => updateLabOrderItemAnalyte(analyte.id, patch),
    onSuccess: () => {
      setError(null)
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1800)
      qc.invalidateQueries({ queryKey: ['lab-order', orderId] })
    },
    onError: (e) => setError(e),
  })

  function persistIfDirty() {
    if (!editable || !analyte.id) return
    const dirty =
      value !== (analyte.result_value || '') ||
      unit  !== (analyte.result_unit  || '') ||
      ref   !== (analyte.reference_range || '') ||
      flag  !== (analyte.abnormal_flag || '')
    if (!dirty) return
    mut.mutate({
      result_value: value,
      result_unit: unit,
      reference_range: ref,
      abnormal_flag: flag,
    })
  }

  const savedFlag = analyte.abnormal_flag || ''
  const isAbnormal = ['L', 'H', 'LL', 'HH', 'A'].includes(savedFlag)
  const isCritical = ['LL', 'HH', 'A'].includes(savedFlag)
  const rowTint = isCritical
    ? 'bg-rose-50/70'
    : isAbnormal
      ? 'bg-amber-50/50'
      : ''

  const inputClass =
    'w-full border border-slate-300 focus:border-wantok-600 focus:outline-none ' +
    'rounded px-2 py-1 text-sm disabled:bg-slate-100 disabled:text-slate-500'

  return (
    <tr className={`border-b border-slate-100 align-top ${rowTint}`}>
      {/* Indented analyte name + small short_code */}
      <td className="px-3 py-1.5 pl-8">
        <div className="text-sm text-slate-800">{analyte.analyte_name}</div>
        <div className="text-[10px] text-slate-400 flex flex-wrap gap-x-2">
          {analyte.short_code && <span className="font-mono">{analyte.short_code}</span>}
          {analyte.loinc_code && <span>LOINC {analyte.loinc_code}</span>}
        </div>
      </td>

      <td className="px-3 py-1.5">
        <input
          type="text"
          value={value}
          disabled={!editable}
          onChange={(e) => setValue(e.target.value)}
          onBlur={persistIfDirty}
          placeholder="—"
          className={inputClass}
        />
      </td>

      <td className="px-3 py-1.5">
        <input
          type="text"
          value={unit}
          disabled={!editable}
          onChange={(e) => setUnit(e.target.value)}
          onBlur={persistIfDirty}
          placeholder={analyte.result_unit || ''}
          className={inputClass}
        />
      </td>

      <td className="px-3 py-1.5">
        <input
          type="text"
          value={ref}
          disabled={!editable}
          onChange={(e) => setRef(e.target.value)}
          onBlur={persistIfDirty}
          placeholder={analyte.reference_range || ''}
          className={inputClass}
        />
      </td>

      <td className="px-3 py-1.5">
        <select
          value={flag}
          disabled={!editable}
          onChange={(e) => {
            const next = e.target.value
            setFlag(next)
            if (editable && analyte.id) {
              mut.mutate({
                result_value: value,
                result_unit: unit,
                reference_range: ref,
                abnormal_flag: next,
              })
            }
          }}
          className={
            'w-full border rounded px-2 py-1 text-sm font-bold text-center ' +
            'disabled:opacity-60 ' +
            flagTone(flag)
          }
        >
          {ABNORMAL_FLAG_OPTIONS.map((opt) => (
            <option key={opt.v || 'none'} value={opt.v}>{opt.label}</option>
          ))}
        </select>
      </td>

      <td className="px-3 py-1.5 text-right">
        {mut.isPending && <span className="text-[11px] text-slate-400">…</span>}
        {!mut.isPending && savedFlash && (
          <span className="text-[11px] font-semibold text-emerald-700">✓</span>
        )}
        {error && (
          <div title={error.message} className="text-[10px] text-rose-700 truncate">
            ⚠ save
          </div>
        )}
      </td>
    </tr>
  )
}
